import React, { useState, useEffect } from 'react';
import CourseScheduleSlots from './CourseScheduleSlots';
import { useAuth } from './AuthContext';

const WEEKDAY_LABELS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

function todayIso() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// Kursinhalte pro Termin + Freigabe-Passwort/Link zum Teilen mit Teilnehmern
function CourseSessionContent({ course }) {
  const { authFetch } = useAuth();
  const [sharePassword, setSharePassword] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const [contentForm, setContentForm] = useState({ title: '', description: '', materials: '', presentation_url: '', content_file: '', notes: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingContent, setSavingContent] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchContent = () => {
    setLoading(true);
    authFetch(`/api/courses/${course.id}/session-content`)
      .then(res => res.json())
      .then(data => {
        setSharePassword(data.data?.share_password || '');
        setEntries(data.data?.entries || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.id]);

  useEffect(() => {
    const existing = entries.find(e => e.occurrence_date === selectedDate);
    setContentForm({
      title: existing?.title || '',
      description: existing?.description || '',
      materials: existing?.materials || '',
      presentation_url: existing?.presentation_url || '',
      content_file: existing?.content_file || '',
      notes: existing?.notes || ''
    });
  }, [selectedDate, entries]);

  const savePassword = () => {
    setSavingPassword(true);
    authFetch(`/api/courses/${course.id}/share-password`, {
      method: 'PUT',
      body: { share_password: sharePassword }
    })
      .then(res => { if (!res.ok) throw new Error(); })
      .catch(() => alert('Fehler beim Speichern des Passworts.'))
      .finally(() => setSavingPassword(false));
  };

  const saveContent = () => {
    setSavingContent(true);
    authFetch(`/api/courses/${course.id}/session-content/${selectedDate}`, {
      method: 'PUT',
      body: contentForm
    })
      .then(res => { if (!res.ok) throw new Error(); })
      .then(fetchContent)
      .catch(() => alert('Fehler beim Speichern des Inhalts.'))
      .finally(() => setSavingContent(false));
  };

  const deleteContent = (date) => {
    if (!window.confirm(`Inhalt für ${date} wirklich löschen?`)) return;
    authFetch(`/api/courses/${course.id}/session-content/${date}`, { method: 'DELETE' })
      .then(fetchContent)
      .catch(() => alert('Fehler beim Löschen.'));
  };

  const shareLink = `${window.location.origin}/kursinhalt/${course.id}`;
  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) return <p className="small text-muted">Lade Kursinhalte...</p>;

  return (
    <div className="mt-3 p-3 bg-light rounded">
      <h6>Kursinhalte &amp; Freigabe</h6>
      <div className="row g-2 align-items-end mb-3">
        <div className="col-md-4">
          <label className="form-label small">Freigabe-Passwort</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={sharePassword}
            onChange={(e) => setSharePassword(e.target.value)}
            placeholder="z.B. Rhino2026"
          />
        </div>
        <div className="col-md-2">
          <button className="btn btn-sm btn-primary" onClick={savePassword} disabled={savingPassword}>
            {savingPassword ? 'Speichert...' : 'Speichern'}
          </button>
        </div>
        {sharePassword && (
          <div className="col-md-6">
            <label className="form-label small">Link zum Teilen</label>
            <div className="input-group input-group-sm">
              <input type="text" className="form-control" value={shareLink} readOnly />
              <button className="btn btn-outline-secondary" onClick={copyLink} type="button">
                {copied ? 'Kopiert!' : 'Kopieren'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="row g-2 mb-2">
        <div className="col-md-3">
          <label className="form-label small">Termin</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
        <div className="col-md-9">
          <label className="form-label small">Thema</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={contentForm.title}
            onChange={(e) => setContentForm(prev => ({ ...prev, title: e.target.value }))}
            placeholder="z.B. Scratch: Schleifen"
          />
        </div>
        <div className="col-12">
          <label className="form-label small">Beschreibung</label>
          <textarea
            className="form-control form-control-sm"
            rows="2"
            value={contentForm.description}
            onChange={(e) => setContentForm(prev => ({ ...prev, description: e.target.value }))}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Material</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={contentForm.materials}
            onChange={(e) => setContentForm(prev => ({ ...prev, materials: e.target.value }))}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Präsentations-Link (externe URL)</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={contentForm.presentation_url}
            onChange={(e) => setContentForm(prev => ({ ...prev, presentation_url: e.target.value }))}
            placeholder="z.B. https://..."
          />
        </div>
        <div className="col-12">
          <label className="form-label small">Lokale HTML-Datei (wird ohne Mitglieder-Login eingebettet)</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={contentForm.content_file}
            onChange={(e) => setContentForm(prev => ({ ...prev, content_file: e.target.value }))}
            placeholder="z.B. scratch-kurs/woche-1/index.html"
          />
          <div className="form-text">
            Pfad relativ zu <code>frontend/public/</code>, per Git gepusht. Wird beim Name+Passwort-Zugriff direkt angezeigt,
            unabhängig davon ob die Datei zusätzlich mitgliedergeschützt unter derselben URL erreichbar ist.
          </div>
        </div>
        <div className="col-12">
          <label className="form-label small">Notizen (intern, nicht sichtbar für Teilnehmer)</label>
          <textarea
            className="form-control form-control-sm"
            rows="2"
            value={contentForm.notes}
            onChange={(e) => setContentForm(prev => ({ ...prev, notes: e.target.value }))}
          />
        </div>
        <div className="col-12">
          <button className="btn btn-sm btn-success" onClick={saveContent} disabled={savingContent}>
            {savingContent ? 'Speichert...' : `Inhalt für ${selectedDate} speichern`}
          </button>
        </div>
      </div>

      {entries.length > 0 && (
        <div className="mt-3">
          <label className="form-label small d-block">Bisherige Einträge</label>
          <ul className="list-group list-group-flush">
            {entries.map(entry => (
              <li key={entry.occurrence_date} className="list-group-item d-flex justify-content-between align-items-center px-0 py-1">
                <span
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedDate(entry.occurrence_date)}
                  className="small"
                >
                  <strong>{entry.occurrence_date}</strong>{entry.title ? ` – ${entry.title}` : ''}
                </span>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => deleteContent(entry.occurrence_date)}
                >
                  &times;
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Manuelle Freischaltung: Admin bucht Mitglieder direkt fuer einen Termin ein,
// unabhaengig vom Selfservice-Kontingent im Kursplan.
function CourseParticipants({ course }) {
  const { authFetch } = useAuth();
  const [selectedSlotId, setSelectedSlotId] = useState(course.slots?.[0]?.id || '');
  const [date, setDate] = useState(todayIso());
  const [bookings, setBookings] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    authFetch('/api/members')
      .then(res => res.json())
      .then(data => setMembers(data.data || []))
      .catch(() => {});
  }, []);

  const loadBookings = () => {
    setLoading(true);
    authFetch('/api/course-bookings')
      .then(res => res.json())
      .then(data => {
        const all = data.data || [];
        setBookings(all.filter(b => b.slot_id === selectedSlotId && b.date === date));
      })
      .catch(() => setError('Fehler beim Laden der Freischaltungen'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (selectedSlotId && date) loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlotId, date]);

  const addMember = () => {
    if (!selectedMemberId || !selectedSlotId || !date) return;
    authFetch('/api/course-bookings', {
      method: 'POST',
      body: { slot_id: selectedSlotId, date, member_id: parseInt(selectedMemberId, 10) }
    })
      .then(res => { if (!res.ok) throw new Error(); })
      .then(() => { setSelectedMemberId(''); loadBookings(); })
      .catch(() => alert('Fehler beim Freischalten.'));
  };

  const removeMember = (bookingId) => {
    authFetch(`/api/course-bookings/${bookingId}`, { method: 'DELETE' })
      .then(res => { if (!res.ok) throw new Error(); })
      .then(loadBookings)
      .catch(() => alert('Fehler beim Entfernen.'));
  };

  if (!course.slots || course.slots.length === 0) {
    return (
      <div className="mt-3 p-3 bg-light rounded">
        <h6>Teilnehmer freischalten</h6>
        <p className="small text-muted mb-0">Erst Wochentermine hinterlegen, um Teilnehmer freizuschalten.</p>
      </div>
    );
  }

  const bookedMemberIds = new Set(bookings.map(b => b.member_id));
  const availableMembers = members.filter(m => !bookedMemberIds.has(m.id));

  return (
    <div className="mt-3 p-3 bg-light rounded">
      <h6>Teilnehmer freischalten</h6>
      <p className="text-muted small">
        Buche ein Mitglied direkt fuer einen Termin ein, unabhaengig vom Wochenkontingent im Kursplan.
      </p>
      <div className="row g-2 align-items-end mb-3">
        <div className="col-md-4">
          <label className="form-label small">Termin</label>
          <select
            className="form-select form-select-sm"
            value={selectedSlotId}
            onChange={(e) => setSelectedSlotId(parseInt(e.target.value, 10))}
          >
            {course.slots.map(s => (
              <option key={s.id} value={s.id}>
                {WEEKDAY_LABELS[s.weekday]} {s.time.slice(0, 5)} Uhr
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label small">Datum</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label small">Mitglied</label>
          <select
            className="form-select form-select-sm"
            value={selectedMemberId}
            onChange={(e) => setSelectedMemberId(e.target.value)}
          >
            <option value="">-- Mitglied waehlen --</option>
            {availableMembers.map(m => (
              <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
            ))}
          </select>
        </div>
        <div className="col-md-1">
          <button className="btn btn-sm btn-primary" onClick={addMember} disabled={!selectedMemberId}>
            +
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-1 px-2 small">{error}</div>}

      {loading ? (
        <p className="small text-muted mb-0">Lade...</p>
      ) : bookings.length === 0 ? (
        <p className="small text-muted mb-0">Noch niemand fuer diesen Termin freigeschaltet.</p>
      ) : (
        <ul className="list-group list-group-flush">
          {bookings.map(b => (
            <li key={b.id} className="list-group-item d-flex justify-content-between align-items-center px-0 py-1">
              <span className="small">{b.first_name} {b.last_name}</span>
              <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeMember(b.id)}>
                &times;
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CourseManager() {
  const [courses, setCourses] = useState([]);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSlots, setEditSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCourses = () => {
    setLoading(true);
    fetch('/api/courses')
      .then(response => response.json())
      .then(data => {
        setCourses(data.data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const toggleExpand = (courseId) => {
    setExpandedCourse(expandedCourse === courseId ? null : courseId);
  };

  const startEdit = (course) => {
    setEditingCourse(course.id);
    setEditForm({
      name: course.name,
      slug: course.slug || '',
      description: course.description || '',
      max_attendees: course.max_attendees,
      color: course.color || '#ffffff',
      image: course.image || '',
      start_date: course.start_date || '',
      end_date: course.end_date || ''
    });
    setEditSlots(
      (course.slots && course.slots.length > 0)
        ? course.slots.map(s => ({ weekday: s.weekday, time: s.time.slice(0, 5), duration: s.duration }))
        : [{ weekday: 1, time: '', duration: '1.5' }]
    );
  };

  const cancelEdit = () => {
    setEditingCourse(null);
    setEditForm({});
    setEditSlots([]);
  };

  const saveEdit = (courseId) => {
    if (!editForm.start_date) {
      alert('Bitte ein Startdatum angeben.');
      return;
    }
    if (editForm.end_date && editForm.end_date < editForm.start_date) {
      alert('Das Enddatum darf nicht vor dem Startdatum liegen.');
      return;
    }
    if (editSlots.some(slot => !slot.time || !slot.duration)) {
      alert('Bitte für alle Wochentermine Uhrzeit und Dauer angeben.');
      return;
    }

    fetch(`/api/courses/${courseId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...editForm,
        max_attendees: parseInt(editForm.max_attendees, 10),
        slots: editSlots.map(slot => ({
          weekday: slot.weekday,
          time: slot.time,
          duration: parseFloat(slot.duration)
        }))
      })
    })
      .then(response => {
        if (!response.ok) throw new Error('Fehler beim Speichern');
        return response.json();
      })
      .then(() => {
        fetchCourses();
        setEditingCourse(null);
        setEditForm({});
        setEditSlots([]);
      })
      .catch(err => alert('Fehler: ' + err.message));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const deleteCourse = (courseId, courseName) => {
    if (!window.confirm(`Möchten Sie den Kurs "${courseName}" wirklich löschen?`)) {
      return;
    }

    fetch(`/api/courses/${courseId}`, { method: 'DELETE' })
      .then(response => {
        if (!response.ok) throw new Error('Fehler beim Löschen');
        return response.json();
      })
      .then(() => {
        setCourses(courses.filter(c => c.id !== courseId));
        setExpandedCourse(null);
      })
      .catch(err => alert('Fehler: ' + err.message));
  };

  if (loading) return <p>Lade Kurse...</p>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="card">
      <div className="card-body">
        <h5 className="card-title">Kurse verwalten</h5>

        {courses.length === 0 ? (
          <p className="text-muted">Keine Kurse vorhanden.</p>
        ) : (
          <div className="list-group">
            {courses.map(course => (
              <div key={course.id} className="list-group-item">
                {editingCourse === course.id ? (
                  <div className="p-2">
                    <h6>Kurs bearbeiten</h6>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <label className="form-label small">Name</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          name="name"
                          value={editForm.name}
                          onChange={handleEditChange}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">Lernportal-Kennung (Slug)</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          name="slug"
                          value={editForm.slug}
                          onChange={handleEditChange}
                          placeholder="z.B. scratch-kurs"
                        />
                        <div className="form-text">
                          Nur fuer Kurse mit eigenem Lernportal (Scratch/KI/Python). Steuert den "Kurs oeffnen"-Link
                          in "Mein Bereich", unabhaengig vom Namen oben.
                        </div>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">Max. Teilnehmer</label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          name="max_attendees"
                          value={editForm.max_attendees}
                          onChange={handleEditChange}
                          required
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small">Farbe</label>
                        <input
                          type="color"
                          className="form-control form-control-sm form-control-color"
                          name="color"
                          value={editForm.color}
                          onChange={handleEditChange}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">Startdatum</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          name="start_date"
                          value={editForm.start_date}
                          onChange={handleEditChange}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">Enddatum (optional, leer lassen für laufende Kurse)</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          name="end_date"
                          value={editForm.end_date}
                          onChange={handleEditChange}
                        />
                      </div>
                      <div className="col-12">
                        <CourseScheduleSlots slots={editSlots} onChange={setEditSlots} />
                      </div>
                      <div className="col-12">
                        <label className="form-label small">Beschreibung</label>
                        <textarea
                          className="form-control form-control-sm"
                          name="description"
                          rows="2"
                          value={editForm.description}
                          onChange={handleEditChange}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label small">Bild-URL</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          name="image"
                          value={editForm.image}
                          onChange={handleEditChange}
                        />
                      </div>
                      <div className="col-12 mt-2">
                        <button
                          className="btn btn-success btn-sm me-2"
                          onClick={() => saveEdit(course.id)}
                        >
                          Speichern
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={cancelEdit}
                        >
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center">
                      <div
                        style={{ cursor: 'pointer', flex: 1 }}
                        onClick={() => toggleExpand(course.id)}
                      >
                        <span
                          className="badge me-2"
                          style={{ backgroundColor: course.color || '#6c757d' }}
                        >
                          &nbsp;
                        </span>
                        <strong>{course.name}</strong>
                        <small className="text-muted ms-2">
                          (max. {course.max_attendees} Teilnehmer)
                        </small>
                        <span className="ms-2">
                          {expandedCourse === course.id ? '▼' : '▶'}
                        </span>
                      </div>
                      <div>
                        <button
                          className="btn btn-outline-primary btn-sm me-1"
                          onClick={() => startEdit(course)}
                        >
                          Bearbeiten
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => deleteCourse(course.id, course.name)}
                        >
                          Löschen
                        </button>
                      </div>
                    </div>

                    {expandedCourse === course.id && (
                      <div className="mt-3 ps-4">
                        <p className="text-muted small">{course.description}</p>
                        <p className="small">
                          <strong>Zeitraum:</strong> {course.start_date || '–'}{' '}
                          {course.end_date ? `bis ${course.end_date}` : '(laufend, kein Enddatum)'}
                        </p>
                        <p className="small">
                          <strong>Wochentermine:</strong>{' '}
                          {course.slots && course.slots.length > 0
                            ? course.slots.map((s, i) => (
                                <span key={s.id} className="badge bg-secondary me-1">
                                  {WEEKDAY_LABELS[s.weekday]} {s.time.slice(0, 5)} ({s.duration}h)
                                </span>
                              ))
                            : <span className="text-muted">Keine Termine hinterlegt.</span>}
                        </p>
                        <CourseSessionContent course={course} />
                        <CourseParticipants course={course} />
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CourseManager;
